// The Monitor's AI-use taxonomy (ClassOverview's Sequences view): the behaviour categories a
// student's session falls into, from their analytics (computeStudentAnalytics). Plain
// functions, so the experiment page's Overview can count them across every condition.

// Window-level categories (pre-query + all-windows modes)
export const WINDOW_TAXONOMY = {
  'ask-quick':       { label: 'Ask Without Trying',            color: '#6b7280' },
  'ask-extended':    { label: 'Ask Without Trying (Extended)', color: '#6b7280' },
  'ran-no-debug':    { label: 'Ran Tests, No Debug',           color: '#6b7280' },
  'debugged-tested': { label: 'Debugged and Tested',           color: '#6b7280' },
  'debug-loop':      { label: 'Debug Loop',                    color: '#6b7280' },
  'code-no-test':    { label: 'Wrote Code, Never Tested',      color: '#6b7280' },
}

// Session-level categories (behavior mode only)
export const SESSION_TAXONOMY = {
  'no-ai':              { label: 'No AI Used',                      color: '#6b7280' },
  'repeated-no-effort': { label: 'Repeated Queries Without Trying', color: '#6b7280' },
  'query-burst':        { label: 'Query Burst',                     color: '#6b7280' },
}

export const TAXONOMY = { ...SESSION_TAXONOMY, ...WINDOW_TAXONOMY }

function classifyWindow(windowSegments, windowEvents) {
  const ids      = new Set((windowSegments || []).map(s => s.behavior?.id).filter(Boolean))
  const runs     = windowEvents.filter(e => e.type === 'TERMINAL_RUN').length
  const hasImpl  = ids.has('implementing')
  const hasDebug = ids.has('debugging')
  const windowMs = windowEvents.length > 1
    ? windowEvents[windowEvents.length - 1].timestamp - windowEvents[0].timestamp
    : 0

  // Count debug→run cycles: TERMINAL_ERROR → code edit → TERMINAL_RUN
  let cycles = 0, sawError = false, sawEditAfterError = false
  for (const e of windowEvents) {
    if (e.type === 'TERMINAL_ERROR') { sawError = true; sawEditAfterError = false }
    else if (sawError && (e.type === 'CODE_TYPE' || e.type === 'CODE_INDENT')) sawEditAfterError = true
    else if (sawEditAfterError && e.type === 'TERMINAL_RUN') { cycles++; sawError = false; sawEditAfterError = false }
  }

  if (hasDebug && runs >= 3 && cycles >= 2) return 'debug-loop'
  if (hasDebug && runs > 0)                 return 'debugged-tested'
  if (hasImpl && runs > 0)                  return 'ran-no-debug'
  if (hasImpl && runs === 0)                return 'code-no-test'
  if (runs > 0)                             return 'ran-no-debug'
  if (windowMs < 30000)                     return 'ask-quick'
  return 'ask-extended'
}

// Session-level classifiers for behavior mode
function getSessionCategories(analytics) {
  const { filteredEvents } = analytics || {}
  if (!filteredEvents || filteredEvents.length === 0) return []

  const hasQueries = filteredEvents.some(e => e.type === 'CHAT_QUERY')
  if (!hasQueries) return ['no-ai']

  const cats = []
  const queryEvents = filteredEvents.filter(e => e.type === 'CHAT_QUERY')

  // Repeated Queries Without Trying: 3+ consecutive queries with no code/run between them
  let consecutiveNoEffort = 0, maxConsecutive = 0
  for (let i = 0; i < queryEvents.length; i++) {
    const prevQuery = i === 0 ? null : queryEvents[i - 1]
    const eventsInGap = prevQuery
      ? filteredEvents.filter(e => e.timestamp > prevQuery.timestamp && e.timestamp < queryEvents[i].timestamp)
      : []
    const hasCode = eventsInGap.some(e => e.type === 'CODE_TYPE' || e.type === 'CODE_INDENT' || e.type === 'TERMINAL_RUN')
    if (!hasCode) { consecutiveNoEffort++; maxConsecutive = Math.max(maxConsecutive, consecutiveNoEffort) }
    else consecutiveNoEffort = 0
  }
  if (maxConsecutive >= 3) cats.push('repeated-no-effort')

  // Query Burst: 3+ queries with < 60s gaps forming a rapid sequence
  let burstLen = 1
  for (let i = 1; i < queryEvents.length; i++) {
    const gap = queryEvents[i].timestamp - queryEvents[i - 1].timestamp
    if (gap < 60000) { burstLen++; if (burstLen >= 3) { cats.push('query-burst'); break } }
    else burstLen = 1
  }

  return cats
}

export function getStudentQueryWindows(analytics) {
  if (!analytics) return []
  const { filteredEvents, autoSegments, replayStartTime, replayDuration } = analytics
  if (!filteredEvents || filteredEvents.length === 0 || !replayDuration) return []

  const queryRelTimes = filteredEvents
    .filter(e => e.type === 'CHAT_QUERY')
    .map(e => e.timestamp - replayStartTime)

  const boundaries = [0, ...queryRelTimes, replayDuration]
  const windows = []

  for (let i = 0; i < boundaries.length - 1; i++) {
    const start = boundaries[i]
    const end   = boundaries[i + 1]
    if (end <= start) continue

    const windowSegments = (autoSegments || []).filter(s => s.startTime < end && s.endTime > start)
    const windowEvents   = filteredEvents.filter(e => {
      const t = e.timestamp - replayStartTime
      return t >= start && t < end
    })

    windows.push({
      startTime: start,
      endTime:   end,
      windowIndex: i,
      isPreQuery: i === 0,
      category: classifyWindow(windowSegments, windowEvents),
    })
  }

  return windows
}

export function getStudentCategories(analytics, mode = 'behavior') {
  const { filteredEvents } = analytics || {}
  const hasQueries = filteredEvents?.some(e => e.type === 'CHAT_QUERY')

  if (mode === 'behavior') {
    const sessionCats = getSessionCategories(analytics)
    const windows = getStudentQueryWindows(analytics)
    const windowCats = Object.keys(WINDOW_TAXONOMY).filter(k => windows.some(w => w.category === k))
    return [...new Set([...sessionCats, ...windowCats])]
  }

  // pre-query and windows modes: exclude no-ai students (no window to show)
  if (!hasQueries) return []

  const windows = getStudentQueryWindows(analytics)
  if (!windows || windows.length === 0) return []

  const relevantWindows = mode === 'pre-query' ? [windows[0]] : windows
  const found = new Set(relevantWindows.map(w => w.category))
  return Object.keys(WINDOW_TAXONOMY).filter(k => found.has(k))
}
